import { useQuery } from "@tanstack/react-query";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormAutocomplete } from "../../../../common/form-fields-wrappers";
import { ICustomerDataForm } from "../../../../models/models";
import { fetchProvincesByCountryCode } from "../../../services/http.services";
import countryMappings from "./country-mappings";
import { useEffect, useState } from "react";

const IdIssuingProvinceField = ({ referenceProp, disabled }: { referenceProp: string, disabled: boolean }) => {
	const { t } = useTranslation('network');
	const [isInitial, setIsInitial] = useState(true);

	const formContext = useFormContext();
	const issuingCountry = useWatch({
		control: formContext.control,
		name: `${referenceProp}.idIssuingCountry`
	});

	const [
		idIssuingCountry,
		idIssuingProvince,
		idIssuingProvinceName,
		idIssuingPlace,
	] = useWatch({
		name: [
			`${referenceProp}.idIssuingCountry`,
			`${referenceProp}.idIssuingProvince`,
			`${referenceProp}.idIssuingProvinceName`,
			`${referenceProp}.idIssuingPlace`,
		]
	})

	const provincesQuery = useQuery({
		queryKey: ['ntw_provinces', issuingCountry],
		queryFn: () => {
			return fetchProvincesByCountryCode({code: issuingCountry ?? 'IT'});
		}
	});

	const handleChange = () => {
		if (issuingCountry === 'IT') {
			formContext.setValue(`${referenceProp}.idIssuingPlace`, null);
		}
	}

	useEffect(() => {
		const countryCodeFromPlace = countryMappings[idIssuingPlace?.description];

		const FOREIGN_STATES = {
			country: countryCodeFromPlace,
			codeProvince: "ES",
			description: "Foreign States",
		};

		if (idIssuingCountry === 'IT') {
			if (idIssuingProvince?.country !== 'IT') {
				formContext.setValue(`${referenceProp}.idIssuingProvince`, null);
				formContext.setValue(`${referenceProp}.idIssuingProvinceName`, null);
			}
		} else if (idIssuingCountry === 'FR') {
			if (idIssuingProvince?.country !== 'FR') {
				formContext.setValue(`${referenceProp}.idIssuingProvince`, null);
				formContext.setValue(`${referenceProp}.idIssuingProvinceName`, null);
			}
		} else if (provincesQuery.data && idIssuingProvinceName && idIssuingProvinceName !== FOREIGN_STATES.codeProvince) {
			const prov = provincesQuery.data?.response?.find((p: any) => p.codeProvince === idIssuingProvinceName);
			if (prov) {
				formContext.setValue(`${referenceProp}.idIssuingCountry`, 'IT');
				formContext.setValue(`${referenceProp}.idIssuingProvince`, prov);
				formContext.setValue(`${referenceProp}.idIssuingProvinceName`, prov.codeProvince);
			}
		} else if (!idIssuingCountry?.length) {
			if (isInitial && idIssuingProvince?.country) {
				formContext.setValue(`${referenceProp}.idIssuingCountry`, idIssuingProvince?.country);
				setIsInitial(false);
			} else if (isInitial && idIssuingPlace && countryCodeFromPlace) {
				formContext.setValue(`${referenceProp}.idIssuingCountry`, countryCodeFromPlace);
				setIsInitial(false);
			}
		} else {
			formContext.setValue(`${referenceProp}.idIssuingProvince`, FOREIGN_STATES);
			formContext.setValue(`${referenceProp}.idIssuingProvinceName`, FOREIGN_STATES.codeProvince);
		}
	}, [
		provincesQuery.data,
		idIssuingCountry, idIssuingProvince?.codeProvince, idIssuingProvinceName
	]);

	return (
		<FormAutocomplete<ICustomerDataForm>
			name={`${referenceProp}.idIssuingProvince`}
			label={t("customerData.form.label.idIssuingProvince")}
			options={provincesQuery.data?.response ?? []}
			isOptionEqualToValue={(option: any, value: any) => option.codeProvince === value.codeProvince}
			getOptionLabel={(option) => option.description}
			fullWidth
			loading={provincesQuery.isInitialLoading}
			onChange={handleChange}
			disabled={disabled}
		/>
	)
}

export default IdIssuingProvinceField;
